import { useState } from 'react';
import { compress, decompress } from 'compress-json';
import { cleanedObject, isRunningInProd } from '../utils/utils';

function useScorm() {
  const [previous, setPrevious] = useState({});
  const emulateScorm = !isRunningInProd();

  function setupScormScripts() {
    return new Promise((resolve, reject) => {
      const scripts = ['scripts/APIWrapper.js', 'scripts/AUFunctions.js'];

      const onLoadPromises = [];

      scripts.forEach((file) => {
        let script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;

        script.src = file;
        onLoadPromises.push(
          new Promise((resolve, reject) => {
            script.onload = function () {
              resolve();
            };
          })
        );
        document.body.appendChild(script);
      });

      Promise.all(onLoadPromises)
        .then((res) => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  async function initializeScormAsync({ emulatedScormData }) {
    console.log('initializeScormAsync');
    let scormData;

    return new Promise((resolve, reject) => {
      // 1. try without interval:
      try {
        scormData = initializeScorm({ emulatedScormData });
        return resolve(scormData);
      } catch (err) {
        // do nothing, will continue below
        console.error(err);
        console.warn(`initial attempt failed, retrying...`);
      }

      // 2. start retrying process:
      const maxRetry = 5;
      let curRetry = 0;
      let lastError;
      const interval = setInterval(() => {
        if (curRetry >= maxRetry) {
          clearInterval(interval);
          return reject(lastError);
        }
        try {
          scormData = initializeScorm({ emulatedScormData });
          clearInterval(interval);
          return resolve(scormData);
        } catch (err) {
          console.error(err);
          console.warn(`attempt ${curRetry}/${maxRetry} failed`);
          lastError = err;
          curRetry++;
        }
      }, 1000);
    });
  }

  function startScormEmulator({ emulatedScormData = {} }) {
    console.log('[startScormEmulator]');
    if (!window.getAPIHandle)
      window.getAPIHandle = () => {
        console.log('[getAPIHandle]');
        return true;
      };
    if (!window.doLMSGetValue)
      window.doLMSGetValue = (arg) => {
        console.log('[doLMSGetValue]', arg);
        switch (arg) {
          case 'cmi.core.lesson_location':
            return emulatedScormData?.lessonLocation || '';
          case 'cmi.core.student_id':
            return emulatedScormData?.studentId || 'student_id';
          case 'cmi.core.student_name':
            return emulatedScormData?.studentName || 'Student Name';
          default:
            return '';
        }
      };
    if (!window.doLMSSetValue)
      window.doLMSSetValue = (arg, val) =>
        console.log('[doLMSSetValue]', arg, val);
    if (!window.doLMSCommit)
      window.doLMSCommit = () => console.log('[doLMSCommit]');
    if (!window.loadPage) window.loadPage = () => console.log('[loadPage]');
    if (!window.unloadPage)
      window.unloadPage = () => console.log('[unloadPage]');
    if (!window.setLessonStatus)
      window.setLessonStatus = (arg, val) =>
        console.log('[setLessonStatus]', arg, val);
  }

  function initializeScorm({ emulatedScormData }) {
    if (emulateScorm) startScormEmulator({ emulatedScormData });

    try {
      // first, check if SCORM api exists
      if (!window.getAPIHandle())
        throw Error("Unable to locate the LMS's API implementation");

      // initialize
      window.loadPage();

      // get persisted data
      const lessonLocation = handleDoLMSGetValue('cmi.core.lesson_location');
      const suspendData = handleDoLMSGetValue('cmi.suspend_data');
      const lessonStatus = handleDoLMSGetValue('cmi.core.lesson_status');
      const scoreRaw = handleDoLMSGetValue('cmi.core.score.raw', {
        ignoreErrors: true,
      });
      const studentId = handleDoLMSGetValue('cmi.core.student_id');
      const studentName = handleDoLMSGetValue('cmi.core.student_name');

      // set unload event
      window.addEventListener('unload', (event) => window.unloadPage());
      window.addEventListener('beforeunload', (event) => window.unloadPage());

      return {
        lessonLocation,
        suspendData,
        lessonStatus,
        scoreRaw,
        studentId,
        studentName,
      };
    } catch (err) {
      throw new Error(err.message || err);
    }
  }

  function setCompleted(callback) {
    setLessonStatus('completed');
    callback && callback('setCompleted');
  }

  function setPassed(callback) {
    setLessonStatus('passed');
    callback && callback('setPassed');
  }

  function setFailed(callback) {
    setLessonStatus('failed');
    callback && callback('setFailed');
  }

  function setLessonStatus(lessonStatus) {
    if (lessonStatus === previous.lessonStatus) return;
    window.doLMSSetValue('cmi.core.lesson_status', lessonStatus);
    commit();
    setPrevious({ ...previous, lessonStatus });
  }

  function setLessonLocation(lessonLocation, callback) {
    if (lessonLocation === previous.lessonLocation) return;
    window.doLMSSetValue('cmi.core.lesson_location', lessonLocation);
    commit();
    setPrevious({ ...previous, lessonLocation });
    callback && callback('setLessonLocation');
  }

  function setSuspendData(suspendData) {
    suspendData = cleanedObject(suspendData); // remove undefined
    suspendData = compress(suspendData);
    suspendData = JSON.stringify(suspendData);
    if (suspendData === previous.suspendData) return;
    window.doLMSSetValue('cmi.suspend_data', suspendData);
    commit();
    setPrevious({ ...previous, suspendData });
  }

  function getSuspendData(suspendData) {
    suspendData = JSON.parse(suspendData);
    suspendData = decompress(suspendData);
    return suspendData;
  }

  function setScore(score, callback) {
    if (score === previous.score) return;
    // order changed according with KnowBe4 logs
    window.doLMSSetValue('cmi.core.score.min', 0);
    window.doLMSSetValue('cmi.core.score.max', 100);
    window.doLMSSetValue('cmi.core.score.raw', score);
    window.doLMSSetValue('cmi.core.score.scaled', score / 100);
    commit();
    setPrevious({ ...previous, score });
    callback && callback('setScore', { score });
  }

  function exitScorm(callback) {
    window.unloadPage();

    // try to close window
    try {
      window.close();
    } catch (err) {
      try {
        // eslint-disable-next-line
        parent.window.close();
      } catch (err) {
        try {
          // eslint-disable-next-line
          top.window.close();
        } catch (err) {
          console.error(err);
        }
      }
    }

    callback && callback('exitScorm');
  }

  function commit() {
    window.doLMSCommit();
  }

  // why ignoreErrors? because there's a known issue on KnowBe4 that throws an error 403 when try to read empty score
  function handleDoLMSGetValue(name, options = { ignoreErrors: false }) {
    try {
      return window.doLMSGetValue(name);
    } catch (err) {
      if (options?.ignoreErrors) {
        console.warn(err?.message);
        return;
      }
      throw err;
    }
  }

  return {
    setupScormScripts,
    initializeScorm,
    initializeScormAsync,
    setCompleted,
    setPassed,
    setFailed,
    setLessonLocation,
    setSuspendData,
    getSuspendData,
    setScore,
    exitScorm,
  };
}

export default useScorm;
